<app-logo-area [breadcrumbs]="breadCrumbs()"></app-logo-area>
<main>
  <app-headline-section [title]="'Remote Desktop'" [loading]="loading$()">
  </app-headline-section>

  <section class="cl-section cl-section-regular">
    <div class="container">
      <div class="row">
        <div class="col-12 col-lg-8">
          <p class="cl-p-large">Manage desktop hosts and editing workspaces.</p>
        </div>
      </div>
    </div>
  </section>

  <section class="cl-section cl-section-regular">
    <div class="container">
      <div class="cl-table-scroll">
        <table class="cl-table-data cp-crd-table">
          <thead>
          <tr>
            <th class="cl-table-head">Pool</th>
            <th class="cl-table-head">Name</th>
            <th class="cl-table-head">State</th>
            <th class="cl-table-head">Details</th>
            <th class="cl-table-head cl-th-links"></th>
          </tr>
          </thead>
          <tbody>
          @if (listState$()?.kind === 'no-access') {
            <tr class="cl-tr-no-data it-crd-no-access"><td colspan="100">This organization has no access to remote desktops.</td></tr>
          } @else if (listState$()?.kind === 'error') {
            <tr class="cl-tr-no-data it-crd-load-error"><td colspan="100">Could not load desktop hosts and workspaces: {{ $any(listState$()).message }}</td></tr>
          } @else if (combined$() === undefined) {
            <tr class="cl-tr-no-data"><td colspan="100"><div class="cl-spinner">Loading ...<i class="fas fa-spinner fa-spin"></i></div></td></tr>
          } @else if (regionView$().length === 0) {
            <tr class="cl-tr-no-data"><td colspan="100">No desktop hosts or editing workspaces found.</td></tr>
          } @else {
            @for (rv of regionView$(); track rv.region) {
              <!-- Region group: was a heading above a table of its own -->
              <tr class="region-row">
                <td colspan="100"><h3 class="cl-h4">{{ rv.city }} <small>({{ rv.region }})</small></h3></td>
              </tr>

              <!-- Editing workspaces this browser asked for that have no volume yet -->
              @for (row of rv.pending; track row.entry.processId) {
                <tr>
                  <td>{{ row.entry.pool }}</td>
                  <td>
                    <i class="fas fa-hdd cl-faded" style="margin-right: 0.4em"></i>
                    <strong>{{ row.entry.mountPoint }}</strong>
                    <br><small class="cl-faded">{{ row.entry.availabilityZone }} &middot; {{ row.entry.contents.length }} item(s)</small>
                  </td>
                  <td>
                    @if (row.process?.state === 'FAILED') {
                      <span class="cl-badge cl-badge-sm cl-color-brick">Failed</span>
                    } @else {
                      <span class="cl-badge cl-badge-sm cl-color-honey">Being created</span>
                    }
                  </td>
                  <td>
                    <app-date [value]="formatTimestamp(row.entry.startedAt / 1000)" />
                    <br><small [class.cl-color-brick]="row.process?.state === 'FAILED'">{{ row.process?.message || 'Starting …' }}</small>
                  </td>
                  <td class="cl-td-links" width="1%">
                    @for (folder of row.sourceFolders; track folder.link) {
                      <!-- the listing view: it shows each file's archive/restore state, the asset view does not -->
                      <a class="cl-a-btn" [routerLink]="folder.link" [queryParams]="{view: 'listing'}" [title]="folder.link">
                        {{ row.sourceFolders.length === 1 ? 'Open source folder' : 'Open ' + folder.name }}</a>
                    }
                    @if (row.process?.state === 'FAILED') {
                      <a class="cl-a-btn" (click)="dismissPending(row)">Dismiss</a>
                    }
                  </td>
                </tr>
              }

              <!-- Unassigned editing workspaces -->
              @for (ws of rv.unassigned; track ws.id) {
                <tr>
                  <td>{{ ws.pool }}</td>
                  <td>
                    <i class="fas fa-hdd cl-faded" style="margin-right: 0.4em"></i>
                    <strong>{{ ws.mountPoint || '-' }}</strong>
                    <br><small class="cl-faded">{{ ws.volumeSizeGiB }} GiB &middot; {{ ws.availabilityZone }} &middot; {{ volumeId(ws) }}</small>
                  </td>
                  <td>
                    <span class="cl-badge cl-badge-sm" [ngClass]="getEditStatusClass(ws.status)">{{ getEditStatusLabel(ws.status) }}</span>
                  </td>
                  <td>
                    <app-date [value]="ws.createdAt ? formatTimestamp(ws.createdAt) : null" />
                    @if (ws.errorMessage) {
                      <br><small class="cl-color-brick">{{ ws.errorMessage }}</small>
                    }
                  </td>
                  <td class="cl-td-links" width="1%">
                    @if (isMountable(ws) || isMounting(ws)) {
                      @if (isMounting(ws)) {
                        <a class="cl-a-btn cl-a-primary disabled"><i class="fas fa-spinner fa-spin"></i> Mount</a>
                      } @else if (hostsInSameAz(ws).length > 0) {
                        <a class="cl-a-btn cl-a-primary" (click)="showMountDialog(ws)">Mount</a>
                      }
                    }
                    <a class="cl-a-btn" [processButton]="'unmount-' + ws.id + '-DELETE'" (click)="unmountWorkspace(ws, 'DELETE')">Delete</a>
                    <!-- On its own line under the actions: inline, it pushed Delete out of the cell. -->
                    @if (isMountable(ws) && !isMounting(ws) && hostsInSameAz(ws).length === 0) {
                      <small class="cl-faded cp-mount-hint">No running host in {{ ws.availabilityZone }} — start one to mount</small>
                    }
                  </td>
                </tr>
              }

              <!-- Hosts with their mounted workspaces -->
              @for (hv of rv.hosts; track hv.host.instanceId) {
                <!-- Host row -->
                <tr class="host-row">
                  <td>{{ hv.host.pool }}</td>
                  <td>
                    <i class="fas fa-desktop" style="margin-right: 0.4em"></i>
                    <strong>{{ hv.host.name || hv.host.instanceId }}</strong>
                    <br><small class="cl-faded">{{ hv.host.instanceId }} &middot; {{ hv.host.instanceType }} &middot; {{ hv.host.availabilityZone }}</small>
                  </td>
                  <td>
                    @switch (hv.host.state) {
                      @case ('registered') { <span class="cl-badge cl-badge-sm cl-color-leaf">Ready</span> }
                      @case ('in_use') { <span class="cl-badge cl-badge-sm cl-color-leaf">Connected</span> }
                      @case ('not_registered') { <span class="cl-badge cl-badge-sm cl-color-honey">Not Registered</span> }
                      @case ('stopped') { <span class="cl-badge cl-badge-sm cl-color-ash">Stopped</span> }
                      @case ('stopping') { <span class="cl-badge cl-badge-sm cl-color-honey">Stopping</span> }
                      @case ('pending') { <span class="cl-badge cl-badge-sm cl-color-honey">Starting</span> }
                      @case ('terminated') { <span class="cl-badge cl-badge-sm cl-color-brick">Terminated</span> }
                      @default { <span class="cl-badge cl-badge-sm cl-color-ash">{{ hv.host.state }}</span> }
                    }
                    @switch (hv.host.network) {
                      @case ('SEALED') { <span class="cl-badge cl-badge-sm ms-1 cl-color-leaf" title="No internet access: workspaces may be mounted">No internet</span> }
                      @case ('OPEN') { <span class="cl-badge cl-badge-sm ms-1 cl-color-honey" title="Internet access after Setup: cut off when a workspace is mounted">Internet</span> }
                      @case ('SEALING') { <span class="cl-badge cl-badge-sm ms-1 cl-color-honey" title="Internet access is being cut off">Cutting off internet</span> }
                    }
                  </td>
                  <td>
                    <span class="cl-font-monospace">{{ hv.host.publicIp || '' }}</span>
                    @if (hv.host.setupDate) {
                      <br><small class="cl-faded">bootstrapped <app-date [value]="hv.host.setupDate" /></small>
                    } @else {
                      <br><small class="cl-color-honey">not bootstrapped</small>
                    }
                  </td>
                  <td class="cl-td-links" width="1%">
                    @if (hv.host.state === 'stopped') {
                      @if ('contentplatform:repository:Mutation/StartDesktopHost' | isAllowedAnywhere: organization()) {
                        <a class="cl-a-btn cl-a-primary" [processButton]="'start-' + hv.host.instanceId" (click)="startSession(hv.host)">Start</a>
                      }
                      @if ('contentplatform:repository:Mutation/SetupDesktopHost' | isAllowedAnywhere: organization()) {
                        <a class="cl-a-btn" [processButton]="'setup-' + hv.host.instanceId" (click)="setupSession(hv.host, hv.workspaces.length)">Setup</a>
                      }
                    } @else if (hv.host.state === 'registered' || hv.host.state === 'in_use' || hv.host.state === 'not_registered') {
                      @if ((hv.host.state === 'registered' || hv.host.state === 'in_use') && ('contentplatform:repository:Mutation/JoinCrdSession' | isAllowedAnywhere: organization())) {
                        <a class="cl-a-btn cl-a-primary it-crd-connect"
                           [routerLink]="['view', hv.host.pool, hv.host.instanceId]"
                           [queryParams]="{hostName: hv.host.name || hv.host.instanceId, region: hv.host.awsRegion, screens: hv.host.screens ?? undefined}">Connect</a>
                      }
                      @if ('contentplatform:repository:Mutation/SetupDesktopHost' | isAllowedAnywhere: organization()) {
                        <a class="cl-a-btn" [processButton]="'setup-' + hv.host.instanceId" (click)="setupSession(hv.host, hv.workspaces.length)">Setup</a>
                      }
                      @if ('contentplatform:repository:Mutation/StopDesktopHost' | isAllowedAnywhere: organization()) {
                        <a class="cl-a-btn" [processButton]="'stop-' + hv.host.instanceId" (click)="stopSession(hv.host)">Stop</a>
                      }
                    }
                  </td>
                </tr>

                <!-- Mounted workspaces under this host -->
                @for (ws of hv.workspaces; track ws.id) {
                  <tr class="workspace-row">
                    <td>{{ ws.pool }}</td>
                    <td style="padding-left: 2rem">
                      <i class="fas fa-hdd cl-faded" style="margin-right: 0.4em"></i>
                      {{ ws.driveLetter }}: <strong>{{ ws.mountPoint }}</strong>
                      <br><small class="cl-faded" style="padding-left: 1rem">{{ ws.volumeSizeGiB }} GiB &middot; {{ ws.awsRegion }} &middot; {{ volumeId(ws) }}</small>
                    </td>
                    <td>
                      <span class="cl-badge cl-badge-sm" [ngClass]="getEditStatusClass(ws.status)">{{ getEditStatusLabel(ws.status) }}</span>
                    </td>
                    <td><app-date [value]="ws.createdAt ? formatTimestamp(ws.createdAt) : null" /></td>
                    <td class="cl-td-links" width="1%">
                      <a class="cl-a-btn" [processButton]="'unmount-' + ws.id + '-UNMOUNT'" (click)="unmountWorkspace(ws, 'UNMOUNT')">Unmount</a>
                      <a class="cl-a-btn" [processButton]="'unmount-' + ws.id + '-COPY_BACK'" (click)="unmountWorkspace(ws, 'COPY_BACK')">Copy Back</a>
                      <a class="cl-a-btn" [processButton]="'unmount-' + ws.id + '-DELETE'" (click)="unmountWorkspace(ws, 'DELETE')">Delete</a>
                    </td>
                  </tr>
                }
              }

            }
          }
          </tbody>
        </table>
      </div>
    </div>
  </section>
</main>
